/**
 * extensionEnvironment.ts — WP-06 Part 1.
 *
 * Web-clip only works when the companion Chrome extension (chrome-extension/)
 * is running in the same browser: the clip flow postMessages `cnf-clip-pair`
 * and waits for the extension's content script to reply `cnf-clip-pair-ack`.
 *
 * On a phone there is no extension runtime at all, so the ack can never arrive
 * and the "Waiting for clip…" state was permanent by construction.  This module
 * answers "could the extension possibly be here?" so the UI never offers a path
 * that cannot complete.
 *
 * Two independent signals:
 *  1. STATIC  — is this a browser that can host the extension at all?
 *               (mobile browsers cannot; the extension is a desktop Chrome MV3
 *               build and is not distributed for iOS/Android.)
 *  2. RUNTIME — did a real pair attempt already prove the extension absent?
 *               `sendPairMessage()` resolves false after its ack timeout; that
 *               is a definitive "not installed / not active in this browser",
 *               so we remember it for the rest of the session instead of
 *               offering a button that will time out again.
 *
 * The runtime signal is cleared by `resetExtensionUnreachable()` — wired to the
 * user-visible "Try again" action, so a transient failure is never a dead end.
 */

import { logger } from './logger';

const UNREACHABLE_KEY = 'cnf_clip_extension_unreachable';

/** Fallback for browsers where sessionStorage throws (Safari private mode). */
let unreachableFallback = false;

/**
 * UA fragments for platforms with no CNF extension runtime.
 * Deliberately explicit rather than a catch-all "mobile" regex so that a
 * desktop browser is never misclassified and silently loses the feature.
 */
const MOBILE_UA_PATTERNS = [
  /Android/i,
  /iPhone/i,
  /iPad/i,
  /iPod/i,
  /Windows Phone/i,
  /IEMobile/i,
  /Opera Mini/i,
  /Mobile Safari/i,
  /CriOS/i, // Chrome on iOS
  /FxiOS/i, // Firefox on iOS
  /EdgiOS/i, // Edge on iOS
  /Silk/i, // Fire tablets
];

interface UserAgentDataLike {
  mobile?: boolean;
}

/**
 * True when this browser could host the CNF clip extension.
 *
 * `nav` is injectable for tests; production callers pass nothing and get the
 * live navigator.  A missing navigator returns FALSE (and logs): if we cannot
 * tell, we must not render an affordance whose failure mode is an endless
 * spinner.
 */
export function isExtensionCapableBrowser(nav: Navigator | undefined = typeof navigator !== 'undefined' ? navigator : undefined): boolean {
  if (!nav) {
    logger.warn('[clip] no navigator available — treating browser as extension-incapable');
    return false;
  }

  // Client Hints, where available, are authoritative and cheap.
  const uaData = (nav as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    if (uaData.mobile) return false;
  }

  const ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
  if (ua === '') {
    logger.warn('[clip] empty userAgent — treating browser as extension-incapable');
    return false;
  }

  if (MOBILE_UA_PATTERNS.some((re) => re.test(ua))) return false;

  // iPadOS 13+ in "Desktop" mode reports a Macintosh UA. A real Mac reports
  // maxTouchPoints 0; an iPad reports 5.
  const touchPoints = typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0;
  if (/Macintosh/i.test(ua) && touchPoints > 1) return false;

  return true;
}

/** Record that a real pair attempt found no extension in this browser. */
export function markExtensionUnreachable(): void {
  unreachableFallback = true;
  try {
    window.sessionStorage.setItem(UNREACHABLE_KEY, '1');
  } catch {
    // sessionStorage unavailable — the module-level fallback still holds.
  }
}

/** Clear the runtime "no extension" memory (wired to the user's Try again). */
export function resetExtensionUnreachable(): void {
  unreachableFallback = false;
  try {
    window.sessionStorage.removeItem(UNREACHABLE_KEY);
  } catch {
    // nothing to do — fallback already cleared
  }
}

/** True when a pair attempt in this session already proved the extension absent. */
export function isExtensionUnreachable(): boolean {
  if (unreachableFallback) return true;
  try {
    return window.sessionStorage.getItem(UNREACHABLE_KEY) === '1';
  } catch {
    return unreachableFallback;
  }
}

/**
 * The single question the UI asks: should a clip affordance be offered here?
 * Static capability AND no proven-absent runtime signal.
 */
export function canPairWithExtension(): boolean {
  return isExtensionCapableBrowser() && !isExtensionUnreachable();
}
